/**
 * "Set up a Private Pool" — guided onboarding wizard for business/sales.
 *
 * Four plain-language steps (customer → apps → location → capacity) plus a
 * review screen; Create goes live immediately (create pool → adjust app
 * access if a subset was chosen → provision machines). All operator jargon
 * (tenancy, clamps, backends, inclusions) stays behind the curtain:
 *   - private_pool, single tenancy (server synthesizes the project inclusion)
 *   - platform-default GCP machine profile in the chosen geography
 *   - capacity → scaling.max, instant-start sessions → scaling.min
 *   - reactive + backfill demand sources (platform defaults)
 *
 * Design + decisions: .docs/pool-admin-redesign.md (wizards section).
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { id3 } from '@interlucent/ids';
import { usePlatformAdmin } from '../../../hooks';
import { TIMEZONES, USER_TIMEZONE } from '../../../components/profiles/CapacityScheduleEditor';
import {
  CustomerPicker,
  getApiBaseUrl,
  Stepper,
  WizardActions,
  type CustomerSelection,
} from '../../../components/pool-wizards/wizardShared';
import styles from '../../../components/pool-wizards/PoolWizards.module.css';

export const Route = createFileRoute('/admin/pools/new-private-pool')({
  component: NewPrivatePoolWizard,
});

const STEPS = ['Customer', 'Apps', 'Location', 'Capacity', 'Schedule', 'Review'];
const REVIEW_STEP = STEPS.length - 1;

type ScheduleChoice = 'always' | 'business' | 'business-weekends' | 'weekends';

const SCHEDULE_CHOICES: Array<{ key: ScheduleChoice; title: string; detail: string }> = [
  { key: 'always', title: 'Always available', detail: 'Kept ready around the clock — every day, all day' },
  { key: 'business', title: 'Business hours', detail: 'Monday to Friday, during working hours' },
  { key: 'business-weekends', title: 'Business hours + weekends', detail: 'Every day of the week, during working hours' },
  { key: 'weekends', title: 'Weekends only', detail: 'Saturday and Sunday, during working hours' },
];

// Hourly options for the friendly From/To selects.
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

const WEEKDAYS = [1, 2, 3, 4, 5]; // Mon–Fri
const WEEKEND = [0, 6]; // Sun, Sat
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6]; // Sun–Sat (for the always-available 24/7 span)

interface Geography {
  key: string;
  title: string;
  detail: string;
  superRegion: string;
  gcpRegion: string;
  /** Australia has no L4 GPUs — fall back to the T4 machine profile. */
  machineTypeOverride?: string;
  /** Backend cloud for this geography. Defaults to GCP; CoreWeave for CW regions. */
  cloud?: 'gcp' | 'coreweave';
  /** CoreWeave region code (e.g. "ORD1") when cloud === 'coreweave'. */
  cwRegion?: string;
}

const GEOGRAPHIES: Geography[] = [
  { key: 'na-west', title: 'North America — West', detail: 'Hosted in Oregon, USA', superRegion: 'wnam', gcpRegion: 'us-west1' },
  { key: 'na-east', title: 'North America — East', detail: 'Hosted in N. Virginia, USA', superRegion: 'enam', gcpRegion: 'us-east4' },
  { key: 'europe', title: 'Europe', detail: 'Hosted in the Netherlands', superRegion: 'weur', gcpRegion: 'europe-west4' },
  { key: 'apac', title: 'Asia-Pacific', detail: 'Hosted in Tokyo, Japan', superRegion: 'apac', gcpRegion: 'asia-northeast1' },
  { key: 'australia', title: 'Australia', detail: 'Hosted in Sydney', superRegion: 'oc', gcpRegion: 'australia-southeast1', machineTypeOverride: 'n1-standard-8' },
  // CoreWeave ORD1 (Chicago) — kept provider-agnostic + location-first like its
  // siblings. Enabled now that CW backend defaults (namespace, root PVC label,
  // secret-backed kube token) are in place.
  { key: 'cw-ord1', title: 'North America — Central', detail: 'Hosted in Chicago, USA', superRegion: 'enam', gcpRegion: '', cloud: 'coreweave', cwRegion: 'ORD1' },
];

// Ensure a unique pool name: if `base` (case-insensitive) collides with an
// existing pool, append " (2)", " (3)", … until free.
function uniquePoolName(base: string, taken: Set<string>): string {
  const lower = new Set(Array.from(taken, n => n.toLowerCase()));
  if (!lower.has(base.toLowerCase())) return base;
  for (let i = 2; i < 1000; i++) {
    const candidate = `${base} (${i})`;
    if (!lower.has(candidate.toLowerCase())) return candidate;
  }
  return `${base} (${crypto.randomUUID().slice(0, 4)})`;
}

// Mirrors InfrastructureEditor's new-backend defaults (OTel endpoint + v4
// launch protocol). TODO(prod): both belong in pool-infra-defaults.
const DEFAULT_OTEL_ENDPOINT = 'https://otel-dev.interlucent.ai';

// New pools pin to the current shipping orc (PSO-storm fix + WS transport). Bump
// this when a newer orc becomes the fleet default.
const DEFAULT_ORCHESTRATOR_VERSION = '0.18.3';

interface ProjectApp {
  id: string;
  name: string;
  appSlug: string;
}

interface GcpInfraDefaults {
  machineType: string;
  firewallTag: string;
  imageId: string;
  project: string;
  serviceAccount?: string;
}

type CreatePhaseState = 'pending' | 'running' | 'done' | 'error';

function NewPrivatePoolWizard() {
  const { isPlatformAdmin } = usePlatformAdmin();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [customer, setCustomer] = useState<CustomerSelection>({ orgId: '', orgName: '', projectId: '', projectName: '' });
  const [allApps, setAllApps] = useState(true);
  const [projectApps, setProjectApps] = useState<ProjectApp[]>([]);
  const [selectedAppIds, setSelectedAppIds] = useState<Set<string>>(new Set());
  const [geographyKey, setGeographyKey] = useState<string>('');
  const [maxUsers, setMaxUsers] = useState('20');
  const [readySessions, setReadySessions] = useState('2');

  // Schedule step. "always" = a 24/7 (all-day, every-day) floor span; the
  // timed choices keep the instant-start sessions ready only during the
  // chosen hours. Every choice produces a capacity schedule.
  const [scheduleChoice, setScheduleChoice] = useState<ScheduleChoice>('always');
  const [schedStart, setSchedStart] = useState('09:00');
  const [schedEnd, setSchedEnd] = useState('17:00');
  const [schedTimezone, setSchedTimezone] = useState(USER_TIMEZONE || 'UTC');
  const [schedEndDate, setSchedEndDate] = useState(''); // yyyy-mm-dd, optional

  const [gcpDefaults, setGcpDefaults] = useState<GcpInfraDefaults | null>(null);
  const [cwDefaults, setCwDefaults] = useState<{ kubeToken?: string; vsPassword?: string } | null>(null);
  const [defaultsError, setDefaultsError] = useState(false);
  const [existingNames, setExistingNames] = useState<Set<string>>(new Set());

  // Creation progress
  const [creating, setCreating] = useState(false);
  const [createdPoolId, setCreatedPoolId] = useState<string | null>(null);
  const [phases, setPhases] = useState<{ create: CreatePhaseState; apps: CreatePhaseState; provision: CreatePhaseState }>({
    create: 'pending', apps: 'pending', provision: 'pending',
  });
  const [createError, setCreateError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  useEffect(() => {
    if (!isPlatformAdmin) navigate({ to: '/' });
  }, [isPlatformAdmin, navigate]);

  const authHeaders = useCallback(async () => {
    const token = await getToken({ template: 'default' });
    if (!token) throw new Error('Authentication required');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getToken]);

  // Platform machine defaults — required to place machines in the chosen
  // geography. Without them the wizard can't safely create infrastructure.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/pool-infra-defaults`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json() as { defaults?: { gcp?: GcpInfraDefaults; coreweave?: { kubeToken?: string; vsPassword?: string } } };
        if (cancelled) return;
        if (data.defaults?.gcp) setGcpDefaults(data.defaults.gcp);
        else setDefaultsError(true);
        if (data.defaults?.coreweave) setCwDefaults(data.defaults.coreweave);
      } catch {
        if (!cancelled) setDefaultsError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  // Existing pool names, so the wizard can auto-uniquify the proposed name.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/pools`, { headers });
        if (!res.ok) return;
        const data = await res.json() as { pools?: Array<{ name?: string }> };
        const names = (data.pools ?? []).map(p => p.name).filter((n): n is string => !!n);
        if (!cancelled) setExistingNames(new Set(names));
      } catch { /* non-fatal: fall back to the raw proposed name */ }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  // Load the project's apps when the customer is chosen (for step 2).
  useEffect(() => {
    if (!customer.projectId) { setProjectApps([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/projects/${customer.projectId}/apps`, { headers });
        if (!res.ok) return;
        const data = await res.json() as { apps: ProjectApp[] };
        if (!cancelled) setProjectApps(data.apps ?? []);
      } catch { /* step 2 falls back to "all apps" only */ }
    })();
    return () => { cancelled = true; };
  }, [customer.projectId, authHeaders]);

  const geography = GEOGRAPHIES.find(g => g.key === geographyKey) ?? null;
  const maxUsersNum = parseInt(maxUsers, 10);
  const readyNum = parseInt(readySessions, 10);
  const capacityValid = !isNaN(maxUsersNum) && maxUsersNum >= 1 && !isNaN(readyNum) && readyNum >= 0 && readyNum <= maxUsersNum;

  const hasTimeWindow = scheduleChoice !== 'always';
  const todayStr = new Date().toISOString().slice(0, 10);
  const scheduleValid = !hasTimeWindow || (
    schedStart !== schedEnd &&
    (!schedEndDate || schedEndDate >= todayStr)
  );
  const scheduleLabel = SCHEDULE_CHOICES.find(c => c.key === scheduleChoice)?.title ?? '';

  const stepValid = [
    !!(customer.orgId && customer.projectId),
    allApps || selectedAppIds.size > 0,
    !!geography,
    capacityValid,
    scheduleValid,
    true,
  ][step];

  const baseName = customer.orgName && customer.projectName
    ? `${customer.orgName} / ${customer.projectName} Private Pool`
    : 'Private Pool';
  const poolName = uniquePoolName(baseName, existingNames);

  const toggleApp = (appId: string) => {
    setSelectedAppIds(prev => {
      const next = new Set(prev);
      if (next.has(appId)) next.delete(appId);
      else next.add(appId);
      return next;
    });
  };

  // ---- Create & go live ----

  const handleCreate = useCallback(async () => {
    if (!geography || !capacityValid) return;
    const isCw = geography.cloud === 'coreweave';
    if (!isCw && !gcpDefaults) return;
    setCreating(true);
    setCreateError(null);
    const needsAppSubset = !allApps;
    setPhases({ create: 'running', apps: needsAppSubset ? 'pending' : 'done', provision: 'pending' });

    const base = getApiBaseUrl();
    let poolId: string | null = null;
    try {
      const headers = await authHeaders();

      // 1. Create the pool. Single tenancy — the server synthesizes a
      //    project-wide inclusion, so "all apps" needs no extra call.
      const cwRegion = geography.cwRegion ?? 'ORD1';
      const backend = isCw
        ? {
            id: `coreweave-${cwRegion}-${id3()}`,
            cloud: 'coreweave',
            region: cwRegion,
            instanceType: 'RTX_A6000',
            imageId: '',
            weight: 1,
            cwApiServer: 'https://k8s.ord1.coreweave.com',
            cwNamespace: 'tenant-kv-rd',
            cwRootPvcLabel: 'interlucent/version=v0.1.3-legacy-up, interlucent/region=ord1',
            ...(cwDefaults?.kubeToken ? { cwKubeToken: cwDefaults.kubeToken } : {}),
            cwVsUsername: 'orc-pool-worker',
            ...(cwDefaults?.vsPassword ? { cwVsPassword: cwDefaults.vsPassword } : {}),
            cwGpuCount: 1,
            cwCpuCount: 10,
            cwMemoryGb: 32,
            cwRootDiskSize: '256Gi',
            cwAdditionalDiskSize: '500Gi',
            otelEndpoint: DEFAULT_OTEL_ENDPOINT,
            otelAuth: 'simple',
            orchestratorArgs: '--board-transport ws',
            launchProtocolVersion: 4,
          }
        : {
            id: `gcp-${geography.gcpRegion.replace(/[-_]/g, '')}-${id3()}`,
            cloud: 'gcp',
            region: geography.gcpRegion,
            instanceType: geography.machineTypeOverride ?? gcpDefaults!.machineType,
            imageId: gcpDefaults!.imageId,
            weight: 1,
            gcpProjectId: gcpDefaults!.project,
            gcpFirewallTags: [gcpDefaults!.firewallTag, 'orc-pool-worker'].filter(Boolean),
            ...(gcpDefaults!.serviceAccount ? { gcpServiceAccount: gcpDefaults!.serviceAccount } : {}),
            otelEndpoint: DEFAULT_OTEL_ENDPOINT,
            otelAuth: 'simple',
            orchestratorArgs: '--board-transport ws',
            launchProtocolVersion: 4,
          };
      // Every choice is expressed as a schedule — the instant-start sessions
      // are a floor DURING the span (span.floor), and the pool follows demand
      // (up to max) the rest of the time. "Always available" is just a span
      // that covers all seven days, all day: equal start/end times encode a
      // full 24h window (see the span→entry conversion in OrgWsDO), so the
      // floor never lapses. Timed choices keep their hours and optional
      // recurring-until expiry (end of that day, span timezone).
      const spans = scheduleChoice === 'always'
        ? [{
            id: crypto.randomUUID(),
            type: 'recurring' as const,
            timezone: schedTimezone,
            daysOfWeek: ALL_DAYS,
            startTime: '00:00',
            endTime: '00:00', // equal start/end === all-day (full 24h)
            floor: readyNum,
            description: 'Always available (wizard)',
          }]
        : (scheduleChoice === 'business' ? [WEEKDAYS]
          : scheduleChoice === 'weekends' ? [WEEKEND]
          : [WEEKDAYS, WEEKEND]
          ).map(daysOfWeek => ({
            id: crypto.randomUUID(),
            type: 'recurring' as const,
            timezone: schedTimezone,
            daysOfWeek,
            startTime: schedStart,
            endTime: schedEnd,
            floor: readyNum,
            description: daysOfWeek === WEEKDAYS ? 'Business hours (wizard)' : 'Weekend hours (wizard)',
            ...(schedEndDate ? { until: `${schedEndDate}T23:59:59` } : {}),
          }));

      const settingsJson = JSON.stringify({
        tenancy: 'single',
        orchestratorVersion: DEFAULT_ORCHESTRATOR_VERSION,
        backends: [backend],
        allocationStrategy: 'balanced',
        // Floor is schedule-driven for every choice (including always-available's
        // 24/7 span), so the hard min stays 0 and the scheduled demand source is
        // always on. The span's floor is what keeps `readyNum` warm.
        scaling: { min: 0, max: maxUsersNum, buffer: 0 },
        strategy: { type: 'reactive' },
        demandSources: { reactive: true, scheduled: true, predictive: false, backfill: true },
      });

      const createRes = await fetch(`${base}/api/admin/pools`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: poolName,
          region: geography.superRegion,
          projectId: customer.projectId,
          type: 'private_pool',
          settingsJson,
          ...(spans ? { capacityScheduleJson: JSON.stringify({ poolMode: 'standard', spans }) } : {}),
        }),
      });
      const createData = await createRes.json().catch(() => ({})) as { ok?: boolean; id?: string; error?: string };
      if (!createRes.ok || !createData.id) {
        throw new Error(createData.error || `Pool creation failed (HTTP ${createRes.status})`);
      }
      poolId = createData.id;
      setCreatedPoolId(poolId);
      setPhases(p => ({ ...p, create: 'done' }));

      // 2. If only specific apps were chosen, replace the synthesized
      //    project-wide grant with per-app grants (default versions only).
      if (needsAppSubset) {
        setPhases(p => ({ ...p, apps: 'running' }));
        const memRes = await fetch(`${base}/api/admin/pools/${poolId}/membership`, { headers });
        const mem = await memRes.json().catch(() => ({})) as {
          inclusions?: Array<{ id: string; nodeKind: string; nodeId: string }>;
        };
        const synthesized = (mem.inclusions ?? []).find(
          i => i.nodeKind === 'project' && i.nodeId === customer.projectId,
        );
        const ops: unknown[] = [];
        if (synthesized) ops.push({ kind: 'removeInclusion', id: synthesized.id });
        for (const appId of selectedAppIds) {
          ops.push({ kind: 'addInclusion', payload: { nodeKind: 'app', nodeId: appId, onlyDefaultVersion: true } });
        }
        const applyRes = await fetch(`${base}/api/admin/pools/${poolId}/membership:apply`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ ops }),
        });
        const applyData = await applyRes.json().catch(() => ({})) as { ok?: boolean };
        if (!applyRes.ok || applyData.ok === false) {
          throw new Error('Pool was created, but limiting it to the selected apps failed.');
        }
        setPhases(p => ({ ...p, apps: 'done' }));
      }

      // 3. Start machines.
      setPhases(p => ({ ...p, provision: 'running' }));
      // ensure=true: the create-time config push may already have
      // provisioned the pool; without the flag this call would read as a
      // "refresh" and tear the fresh machines down again.
      const provRes = await fetch(`${base}/api/admin/pools/${poolId}/provision?ensure=true`, { method: 'POST', headers });
      if (!provRes.ok) {
        const provData = await provRes.json().catch(() => ({})) as { error?: string };
        throw new Error(provData.error || 'Pool was created, but starting machines failed.');
      }
      setPhases(p => ({ ...p, provision: 'done' }));
      setSucceeded(true);
    } catch (err: any) {
      setCreateError(err.message || 'Something went wrong');
      setPhases(p => ({
        create: p.create === 'running' ? 'error' : p.create,
        apps: p.apps === 'running' ? 'error' : p.apps,
        provision: p.provision === 'running' ? 'error' : p.provision,
      }));
    } finally {
      setCreating(false);
    }
  }, [geography, gcpDefaults, cwDefaults, capacityValid, allApps, authHeaders, poolName, customer.projectId, selectedAppIds, readyNum, maxUsersNum, hasTimeWindow, scheduleChoice, schedStart, schedEnd, schedTimezone, schedEndDate]);

  if (!isPlatformAdmin) return null;

  // ---- Success screen ----
  if (succeeded && createdPoolId) {
    return (
      <div className={styles.page}>
        <div className={styles.successCard}>
          <h2 className={styles.successTitle}>✓ {poolName} is live</h2>
          <p className={styles.reviewSummary}>
            Machines are starting now — the first sessions are typically ready in a few minutes.
            Capacity costs begin accruing immediately and scale down automatically when idle.
          </p>
        </div>
        <div className={styles.actions}>
          <Link to="/admin/pools" className={styles.secondaryButton}>Back to Pools</Link>
          <Link to="/admin/pools/$poolId" params={{ poolId: createdPoolId }} className={styles.primaryButton}>
            View Pool
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <button className={styles.backLink} onClick={() => navigate({ to: '/admin/pools' })}>
        &larr; Pools
      </button>
      <h1 className={styles.title}>Set up a Private Pool</h1>
      <p className={styles.subtitle}>
        Dedicated streaming capacity for one customer. Takes about a minute — everything can be adjusted later.
      </p>

      <Stepper steps={STEPS} current={step} />

      {defaultsError && (
        <div className={styles.errorBox}>
          Platform machine defaults are unavailable, so the wizard can't place capacity right now.
          Please contact the operations team.
        </div>
      )}

      {/* Step 1 — Customer */}
      {step === 0 && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepQuestion}>Who is this pool for?</h2>
          <p className={styles.stepHint}>Pick the customer's organization and project.</p>
          <CustomerPicker authHeaders={authHeaders} value={customer} onChange={setCustomer} />
        </div>
      )}

      {/* Step 2 — Apps */}
      {step === 1 && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepQuestion}>Which apps should run here?</h2>
          <p className={styles.stepHint}>
            "All apps" is the usual choice — anything the customer adds to this project later is covered automatically.
          </p>
          <label className={styles.optionRow}>
            <input type="radio" name="apps-mode" checked={allApps} onChange={() => setAllApps(true)} />
            <span>
              All apps in {customer.projectName || 'this project'}
              <div className={styles.optionRowNote}>Including apps added in the future</div>
            </span>
          </label>
          <label className={styles.optionRow}>
            <input
              type="radio"
              name="apps-mode"
              checked={!allApps}
              onChange={() => setAllApps(false)}
              disabled={projectApps.length === 0}
            />
            <span>
              Only specific apps
              {projectApps.length === 0 && <div className={styles.optionRowNote}>No apps found in this project yet</div>}
            </span>
          </label>
          {!allApps && projectApps.length > 0 && (
            <div style={{ marginLeft: 'var(--space-5)' }}>
              {projectApps.map(app => (
                <label key={app.id} className={styles.optionRow}>
                  <input
                    type="checkbox"
                    checked={selectedAppIds.has(app.id)}
                    onChange={() => toggleApp(app.id)}
                  />
                  <span>{app.name}</span>
                </label>
              ))}
              <div className={styles.optionRowNote}>
                Apps added to the project later will NOT be covered — someone will need to add them here.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3 — Location */}
      {step === 2 && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepQuestion}>Where are their users?</h2>
          <p className={styles.stepHint}>
            Machines run closest to this location. Pick where most users will connect from.
          </p>
          <div className={styles.cardGrid}>
            {GEOGRAPHIES.map(geo => (
              <button
                key={geo.key}
                type="button"
                className={geo.key === geographyKey ? styles.choiceCardSelected : styles.choiceCard}
                onClick={() => setGeographyKey(geo.key)}
                aria-pressed={geo.key === geographyKey}
              >
                <div className={styles.choiceCardTitle}>{geo.title}</div>
                <div className={styles.choiceCardDetail}>{geo.detail}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 4 — Capacity */}
      {step === 3 && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepQuestion}>How many people can use it at once?</h2>
          <p className={styles.stepHint}>
            The pool grows and shrinks automatically with real usage — this sets the upper limit.
          </p>
          <div className={styles.numberRow}>
            <span>Up to</span>
            <input
              className={styles.numberInput}
              type="number"
              min="1"
              value={maxUsers}
              onChange={e => setMaxUsers(e.target.value)}
              aria-label="Maximum people at the same time"
            />
            <span>people at the same time</span>
          </div>
          <div className={styles.numberRow}>
            <span>Keep</span>
            <input
              className={styles.numberInput}
              type="number"
              min="0"
              value={readySessions}
              onChange={e => setReadySessions(e.target.value)}
              aria-label="Sessions kept ready for instant start"
            />
            <span>sessions ready for instant start</span>
          </div>
          {!capacityValid && (
            <div className={styles.warnLine}>
              "Ready" sessions can't exceed the maximum, and the maximum must be at least 1.
            </div>
          )}
          <div className={styles.hintLine}>
            ≈ {isNaN(maxUsersNum) ? '—' : maxUsersNum} machine{maxUsersNum === 1 ? '' : 's'} when fully busy
            · {isNaN(readyNum) ? '—' : readyNum} machine{readyNum === 1 ? '' : 's'} always running
            {readyNum > 0 ? ' (these cost money even when idle)' : ''}
          </div>
        </div>
      )}

      {/* Step 5 — Schedule (optional) */}
      {step === 4 && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepQuestion}>When will they use it?</h2>
          <p className={styles.stepHint}>
            Optional. With a schedule, the {isNaN(readyNum) ? '' : readyNum} instant-start
            session{readyNum === 1 ? '' : 's'} are kept ready only during these hours —
            outside them the pool follows demand and can scale to zero.
          </p>
          <div className={styles.cardGrid}>
            {SCHEDULE_CHOICES.map(choice => (
              <button
                key={choice.key}
                type="button"
                className={choice.key === scheduleChoice ? styles.choiceCardSelected : styles.choiceCard}
                onClick={() => setScheduleChoice(choice.key)}
                aria-pressed={choice.key === scheduleChoice}
              >
                <div className={styles.choiceCardTitle}>{choice.title}</div>
                <div className={styles.choiceCardDetail}>{choice.detail}</div>
              </button>
            ))}
          </div>

          {hasTimeWindow && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <div className={styles.numberRow}>
                <span>From</span>
                <select
                  className={styles.select}
                  style={{ width: 'auto', marginBottom: 0 }}
                  value={schedStart}
                  onChange={e => setSchedStart(e.target.value)}
                  aria-label="Schedule start time"
                >
                  {HOUR_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <span>to</span>
                <select
                  className={styles.select}
                  style={{ width: 'auto', marginBottom: 0 }}
                  value={schedEnd}
                  onChange={e => setSchedEnd(e.target.value)}
                  aria-label="Schedule end time"
                >
                  {HOUR_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>

              <label className={styles.fieldLabel} htmlFor="sched-tz">Timezone</label>
              <select
                id="sched-tz"
                className={styles.select}
                value={schedTimezone}
                onChange={e => setSchedTimezone(e.target.value)}
              >
                {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
              </select>

              <label className={styles.fieldLabel} htmlFor="sched-end-date">Ends on (optional)</label>
              <input
                id="sched-end-date"
                className={styles.select}
                style={{ width: 'auto', cursor: 'auto' }}
                type="date"
                min={todayStr}
                value={schedEndDate}
                onChange={e => setSchedEndDate(e.target.value)}
              />
              <div className={styles.optionRowNote}>
                Leave empty to repeat every week with no end date. With a date set, the
                schedule stops after that day — handy for pilots and events.
              </div>

              {!scheduleValid && (
                <div className={styles.warnLine}>
                  {schedStart === schedEnd
                    ? 'Start and end time are the same — pick a real window, or choose "Always available".'
                    : 'The end date is in the past.'}
                </div>
              )}
              {readyNum === 0 && (
                <div className={styles.warnLine}>
                  You chose 0 instant-start sessions on the Capacity step, so this schedule
                  keeps nothing warm. Go back and set at least 1 for the schedule to matter.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 6 — Review */}
      {step === REVIEW_STEP && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepQuestion}>Ready to go live?</h2>
          <p className={styles.reviewSummary}>
            A private pool for <strong>{customer.orgName} / {customer.projectName}</strong>, running{' '}
            <strong>{allApps ? 'all apps' : `${selectedAppIds.size} selected app${selectedAppIds.size === 1 ? '' : 's'}`}</strong>,
            hosted in <strong>{geography?.title}</strong>, supporting{' '}
            <strong>up to {maxUsersNum} people at once</strong>
            {readyNum > 0 ? (
              hasTimeWindow
                ? <> with <strong>{readyNum} session{readyNum === 1 ? '' : 's'} kept ready {scheduleLabel.toLowerCase()}, {schedStart}–{schedEnd} ({schedTimezone.replace(/_/g, ' ')})</strong></>
                : <> with <strong>{readyNum} session{readyNum === 1 ? '' : 's'} kept ready around the clock</strong></>
            ) : null}
            {hasTimeWindow && schedEndDate ? <>, <strong>until {schedEndDate}</strong></> : null}.
          </p>
          <ul className={styles.reviewList}>
            <li>Pool name: {poolName}</li>
            <li>Machines start immediately after you click Create — costs begin right away.</li>
            <li>Everything here can be changed later from the pool page.</li>
          </ul>

          {(creating || createError) && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <ul className={styles.progressList}>
                <li className={phaseClass(phases.create)}>{phaseGlyph(phases.create)} Creating pool</li>
                {!allApps && <li className={phaseClass(phases.apps)}>{phaseGlyph(phases.apps)} Granting app access</li>}
                <li className={phaseClass(phases.provision)}>{phaseGlyph(phases.provision)} Starting machines</li>
              </ul>
            </div>
          )}
          {createError && (
            <div className={styles.errorBox} style={{ marginTop: 'var(--space-3)' }}>
              {createError}
              {createdPoolId && (
                <div style={{ marginTop: 'var(--space-2)' }}>
                  The pool exists — an operator can finish setup from the{' '}
                  <Link to="/admin/pools/$poolId" params={{ poolId: createdPoolId }} style={{ color: 'inherit' }}>
                    pool page
                  </Link>.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <WizardActions
        onBack={step > 0 ? () => setStep(step - 1) : undefined}
        backDisabled={creating}
        onNext={step < REVIEW_STEP ? () => setStep(step + 1) : handleCreate}
        nextLabel={step < REVIEW_STEP ? 'Next' : creating ? 'Creating…' : 'Create Pool'}
        nextDisabled={!stepValid || creating || (step === REVIEW_STEP && (!scheduleValid || (geography?.cloud !== 'coreweave' && (defaultsError || !gcpDefaults))))}
      />
    </div>
  );
}

function phaseClass(s: CreatePhaseState): string {
  if (s === 'done') return styles.progressDone;
  if (s === 'error') return styles.progressError;
  return styles.progressPending;
}

function phaseGlyph(s: CreatePhaseState): string {
  if (s === 'done') return '✓';
  if (s === 'error') return '✗';
  if (s === 'running') return '…';
  return '·';
}
